import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonIcon } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getProp from "@insite/mobius/utilities/getProp";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowRightLongIcon from "@tCentro/Icons/ArrowRightLongIcon";
import QuestionMarkNeedHelpIcon from "@tCentro/Icons/QuestionMarkNeedHelpIcon";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import React, { useState } from "react";
import { connect } from "react-redux";

import { css } from "styled-components";

const enum fields {
    title = "title",
    description = "description",
    exploreButtonLabel = "exploreButtonLabel",
    exploreButtonLink = "exploreButtonLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
        [fields.exploreButtonLabel]: string;
        [fields.exploreButtonLink]: LinkFieldValue;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasCategoryContext;

export interface CENProductNeedHelpStyle {
    container?: InjectableCss;
    titleText?: InjectableCss;
    description?: InjectableCss;
    buttonLabel?: InjectableCss;
    iconStyle?: InjectableCss;
    questionMarkIconStyle?: InjectableCss;
    parentQuestionMark?: InjectableCss;
    closeIconStyles?: IconPresentationProps;
}

export const needHelpStyles: CENProductNeedHelpStyle = {
    closeIconStyles: {
        src: "X",
    },
    container: {
        css: css`
            padding: 16px;
            background: ${COLORS.background.grayShade};
            position: fixed;
            right: 50px;
            z-index: 999;
            top: 250px;
            max-width: 394px;
            max-height: 150px;
            box-shadow: 0 5px 4px 0 rgb(0 0 0 / 26%);
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        top: 255px !important;
                        right: 20px !important;
                    `,
                    css`
                        top: 240px !important;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    titleText: {
        css: css`
            display: flex;
            align-items: center;
            margin-bottom: 8px !important;
            p {
                min-height: 24px;
                width: 100%;
                font-size: ${TYPOGRAPHY.size.base};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                color: ${getProp("theme.typography.p.color")};
                margin-bottom: 0px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        p {
                            font-size: ${TYPOGRAPHY.size.smaller};
                        }
                    `,
                    css`
                        p {
                            font-size: ${TYPOGRAPHY.size.smaller};
                        }
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    description: {
        css: css`
            p {
                text-align: left;
                width: 100%;
                margin-bottom: 21px !important;
                font-size: ${TYPOGRAPHY.size.smaller};
                font-weight: ${TYPOGRAPHY.weight.base};
                line-height: 20px;
                color: ${getProp("theme.typography.p.color")};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        p {
                            font-size: ${TYPOGRAPHY.size.tiny};
                        }
                    `,
                    css`
                        p {
                            font-size: ${TYPOGRAPHY.size.tiny};
                        }
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    buttonLabel: {
        css: css`
            font-size: ${TYPOGRAPHY.size.tiny};
            line-height: 16px;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            color: ${COLORS.link.base};
            cursor: pointer;
        `,
    },
    iconStyle: {
        css: css`
            height: 7.5px;
            width: 12px;
            margin-left: 4px;
            svg {
                height: 7.5px;
                width: 12px;
            }
        `,
    },
    questionMarkIconStyle: {
        css: css`
            height: 18px;
            width: 18px;
            margin-right: 11px;
        `,
    },
    parentQuestionMark: {
        css: css`
            align-items: center;
            border-radius: 30px;
            top: 200px;
            color: #fff;
            background-color: #cac0c0;
            cursor: pointer;
            display: flex;
            justify-content: center;
            position: fixed;
            right: 20px;
            z-index: 999;
            height: 40px;
            width: 40px;
            svg {
                height: 36px;
                width: 36px;
            }
        `,
    },
};

const styles = needHelpStyles;

const PlpPageNeedHelpProduct = ({ category, query, fields }: Props) => {
    const { url, title } = useGetLink(fields.exploreButtonLink);
    const history = useHistory();
    const { isInShell } = useShellContext();
    const replaceText = query || category?.name;
    const description = fields.description.replace("{0}", replaceText || "");

    const [toggle, setToggle] = useState(false);

    const onClick = () => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    return description ? (
        <>
            <ButtonIcon
                src={QuestionMarkNeedHelpIcon}
                onClick={() => setToggle(!toggle)}
                {...styles.parentQuestionMark}
            />
            {toggle && (
                <StyledWrapper {...styles.container}>
                    <Typography {...styles.titleText}>
                        <Icon src={QuestionMarkNeedHelpIcon} {...styles.questionMarkIconStyle} />
                        {parse(transformContentHtml(fields.title), parserOptions)}
                        <Icon {...styles.closeIconStyles} onClick={() => setToggle(!toggle)} />
                    </Typography>
                    <Typography {...styles.description}>
                        {parse(transformContentHtml(description), parserOptions)}
                    </Typography>
                    <Typography {...styles.buttonLabel} onClick={onClick}>
                        {fields.exploreButtonLabel || title || url}
                    </Typography>
                </StyledWrapper>
            )}
        </>
    ) : null;
};

const mapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => {
    const correctedQuery = getProductListDataViewProperty(state, "correctedQuery");
    const { productFilters } = state.pages.productList;
    return {
        category: getCategoryState(state, ownProps.categoryId).value,
        query: correctedQuery || productFilters.query,
    };
};

const productNeedHelp: WidgetModule = {
    component: connect(mapStateToProps)(withCategory(PlpPageNeedHelpProduct)),
    definition: {
        group: "Basic",
        displayName: "CEN Need Help",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.title,
                editorTemplate: "RichTextField",
                displayName: "Title",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.description,
                editorTemplate: "RichTextField",
                displayName: "Description",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.exploreButtonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.exploreButtonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
        ],
    },
};

export default productNeedHelp;
